/**
 * Setting a new password from a reset link (src/pages/auth/ResetPassword.tsx).
 *
 * The password is saved with the sign-in the link itself made, never with the
 * browser's stored sign-in: every tab of the app shares that one, and another tab
 * signed in as a different account can replace it while the reset page is open
 * (2026-11-03: a new password landed on the other account).
 */

/** The sign-in the reset link made: whose password this page changes. */
export interface RecoverySignIn {
  accessToken: string;
  email: string | null;
}

/**
 * Set the password of the person the reset link signed in, with that sign-in's
 * own token, through the sign-in service (PUT /auth/v1/user). Independent of the
 * browser's stored sign-in, which another tab can replace at any moment.
 */
export async function setPasswordForRecovery(recovery: RecoverySignIn, password: string): Promise<string | null> {
  const env = import.meta.env as Record<string, string | undefined>;
  const url = (env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '');
  const anonKey = env.VITE_SUPABASE_ANON_KEY ?? '';
  try {
    const res = await fetch(`${url}/auth/v1/user`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', apikey: anonKey, Authorization: `Bearer ${recovery.accessToken}` },
      body: JSON.stringify({ password }),
    });
    if (res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { msg?: string; message?: string; error_description?: string };
    return body.msg || body.message || body.error_description || `The password was not saved (HTTP ${res.status}).`;
  } catch (err) {
    return err instanceof Error ? err.message : 'The password was not saved.';
  }
}
